'use client';

import { useState } from 'react';
import SeverityBadge, { Severity } from '@/components/ui/SeverityBadge';
import StatusBadge, { IncidentStatus } from '@/components/ui/StatusBadge';
import Icon from '@/components/ui/AppIcon';
import IncidentDetailPanel from './IncidentDetailPanel';
import { toast } from 'sonner';

interface Incident {
  id: string;
  title: string;
  severity: Severity;
  status: IncidentStatus;
  assignee: string;
  assigneeInitials: string;
  affectedAssets: string[];
  firstSeen: string;
  lastUpdated: string;
  mttd: string;
  eventCount: number;
  mitreTactic: string;
  mitreId: string;
  slaBreached: boolean;
  description: string;
  timeline: { ts: string; action: string; user: string; type: 'detection' | 'action' | 'update' | 'escalation' }[];
}

const incidents: Incident[] = [
  {
    id: 'INC-2026-0041',
    title: 'Active C2 Beacon — Workstation HR-14',
    severity: 'critical',
    status: 'investigating',
    assignee: 'Selin Arslan',
    assigneeInitials: 'SA',
    affectedAssets: ['workstation-hr-14', 'core-dns-01'],
    firstSeen: '07:04:12',
    lastUpdated: '07:17:44',
    mttd: '2.3 min',
    eventCount: 847,
    mitreTactic: 'Command & Control',
    mitreId: 'T1071',
    slaBreached: false,
    description: 'Workstation workstation-hr-14 is communicating with known C2 infrastructure at 203.0.113.42 via DNS tunneling. Initial compromise vector suspected to be phishing email attachment opened at 06:41 UTC.',
    timeline: [
      { ts: '07:04:12', action: 'Incident auto-created from C2-BEACON-007 rule correlation', user: 'System', type: 'detection' },
      { ts: '07:06:33', action: 'Incident assigned to Selin Arslan', user: 'AutoAssign', type: 'action' },
      { ts: '07:09:18', action: 'Network isolation initiated on workstation-hr-14', user: 'Selin Arslan', type: 'action' },
      { ts: '07:14:52', action: 'Memory dump captured from affected workstation', user: 'Selin Arslan', type: 'update' },
      { ts: '07:17:44', action: 'Malware sample submitted to sandboxing environment', user: 'Selin Arslan', type: 'update' },
    ],
  },
  {
    id: 'INC-2026-0040',
    title: 'SSH Brute Force — auth-server-01 (1,247 attempts)',
    severity: 'critical',
    status: 'contained',
    assignee: 'Mehmet Yılmaz',
    assigneeInitials: 'MY',
    affectedAssets: ['auth-server-01'],
    firstSeen: '06:48:21',
    lastUpdated: '07:02:17',
    mttd: '1.8 min',
    eventCount: 1247,
    mitreTactic: 'Credential Access',
    mitreId: 'T1110',
    slaBreached: false,
    description: 'Sustained brute force attack against SSH service on auth-server-01 from Tor exit node 185.220.101.47. Source IP has been blocked at perimeter firewall. No successful authentication observed.',
    timeline: [
      { ts: '06:48:21', action: 'Incident created — SSH-BF-001 threshold exceeded', user: 'System', type: 'detection' },
      { ts: '06:50:04', action: 'Assigned to Mehmet Yılmaz', user: 'AutoAssign', type: 'action' },
      { ts: '06:54:33', action: 'Source IP 185.220.101.47 blocked at perimeter FW', user: 'Mehmet Yılmaz', type: 'action' },
      { ts: '07:02:17', action: 'Attack traffic ceased — incident contained', user: 'Mehmet Yılmaz', type: 'update' },
    ],
  },
  {
    id: 'INC-2026-0039',
    title: 'Lateral Movement Detected — Dev Subnet',
    severity: 'critical',
    status: 'investigating',
    assignee: 'Ayşe Kaya',
    assigneeInitials: 'AK',
    affectedAssets: ['workstation-dev-07', 'db-server-02', '10.0.5.0/24'],
    firstSeen: '06:31:09',
    lastUpdated: '07:11:33',
    mttd: '4.1 min',
    eventCount: 312,
    mitreTactic: 'Lateral Movement',
    mitreId: 'T1021',
    slaBreached: true,
    description: 'Attacker pivoting through dev subnet after initial compromise of workstation-dev-07. SMB and WMI-based lateral movement detected toward db-server-02. SLA response time breached.',
    timeline: [
      { ts: '06:31:09', action: 'Lateral movement pattern detected by behavioral rule LATERAL-003', user: 'System', type: 'detection' },
      { ts: '06:35:44', action: 'Escalated to P1 — SLA timer started', user: 'System', type: 'escalation' },
      { ts: '06:41:20', action: 'Assigned to Ayşe Kaya', user: 'SOC Lead', type: 'action' },
      { ts: '07:04:18', action: 'SLA breach — 30 min response threshold exceeded', user: 'System', type: 'escalation' },
      { ts: '07:11:33', action: 'Network segmentation applied to 10.0.5.0/24', user: 'Ayşe Kaya', type: 'action' },
    ],
  },
  {
    id: 'INC-2026-0038',
    title: 'DNS Tunneling — Data Exfiltration Suspected',
    severity: 'high',
    status: 'investigating',
    assignee: 'Selin Arslan',
    assigneeInitials: 'SA',
    affectedAssets: ['core-dns-01', '10.0.1.5'],
    firstSeen: '05:54:33',
    lastUpdated: '06:47:18',
    mttd: '6.2 min',
    eventCount: 2341,
    mitreTactic: 'Exfiltration',
    mitreId: 'T1048',
    slaBreached: false,
    description: 'Anomalous DNS query patterns from core-dns-01 indicating potential data exfiltration via DNS tunneling. Query entropy is significantly elevated. Destination domain flagged in threat intelligence feeds.',
    timeline: [
      { ts: '05:54:33', action: 'DNS-TUNNEL-001 rule triggered — anomalous query volume', user: 'System', type: 'detection' },
      { ts: '06:00:11', action: 'Assigned to Selin Arslan', user: 'AutoAssign', type: 'action' },
      { ts: '06:18:44', action: 'DNS query logs exported for analysis', user: 'Selin Arslan', type: 'update' },
      { ts: '06:47:18', action: 'Threat intel match confirmed — C2 domain blocked', user: 'Selin Arslan', type: 'action' },
    ],
  },
  {
    id: 'INC-2026-0037',
    title: 'RDP Brute Force — rdp-gateway-01',
    severity: 'high',
    status: 'contained',
    assignee: 'Mehmet Yılmaz',
    assigneeInitials: 'MY',
    affectedAssets: ['rdp-gateway-01'],
    firstSeen: '04:12:08',
    lastUpdated: '05:30:44',
    mttd: '3.4 min',
    eventCount: 534,
    mitreTactic: 'Initial Access',
    mitreId: 'T1133',
    slaBreached: false,
    description: 'Brute force attack targeting RDP service on gateway. 534 failed authentication attempts from 3 distinct source IPs. Account lockout policies engaged after 10 attempts.',
    timeline: [
      { ts: '04:12:08', action: 'RDP-BF-002 threshold exceeded — incident created', user: 'System', type: 'detection' },
      { ts: '04:15:30', action: 'Source IPs added to block list', user: 'Mehmet Yılmaz', type: 'action' },
      { ts: '05:30:44', action: 'Attack traffic ceased — incident contained', user: 'Mehmet Yılmaz', type: 'update' },
    ],
  },
  {
    id: 'INC-2026-0036',
    title: 'SQL Injection Campaign — webapp-prod-02',
    severity: 'high',
    status: 'resolved',
    assignee: 'Ayşe Kaya',
    assigneeInitials: 'AK',
    affectedAssets: ['webapp-prod-02', 'db-server-02'],
    firstSeen: '2026-03-15 22:34:17',
    lastUpdated: '2026-03-15 23:58:02',
    mttd: '5.7 min',
    eventCount: 893,
    mitreTactic: 'Initial Access',
    mitreId: 'T1190',
    slaBreached: false,
    description: 'Automated SQL injection campaign targeting login and search endpoints on webapp-prod-02. WAF rules updated to block attack patterns. No evidence of successful data extraction.',
    timeline: [
      { ts: '22:34:17', action: 'SQLI-WEB-004 rule triggered', user: 'System', type: 'detection' },
      { ts: '22:40:08', action: 'WAF rules updated to block SQLi patterns', user: 'Ayşe Kaya', type: 'action' },
      { ts: '23:58:02', action: 'Incident resolved — no data breach confirmed', user: 'Ayşe Kaya', type: 'update' },
    ],
  },
  {
    id: 'INC-2026-0035',
    title: 'Privilege Escalation — mail-server-01',
    severity: 'medium',
    status: 'resolved',
    assignee: 'Selin Arslan',
    assigneeInitials: 'SA',
    affectedAssets: ['mail-server-01'],
    firstSeen: '2026-03-15 18:11:44',
    lastUpdated: '2026-03-15 20:03:29',
    mttd: '8.3 min',
    eventCount: 47,
    mitreTactic: 'Privilege Escalation',
    mitreId: 'T1068',
    slaBreached: false,
    description: 'Suspicious privilege escalation attempt on mail server using known CVE. Patch was not applied during last maintenance window. Emergency patch applied and service restarted.',
    timeline: [
      { ts: '18:11:44', action: 'PRIV-ESC-004 rule triggered', user: 'System', type: 'detection' },
      { ts: '18:20:03', action: 'Emergency patch CVE-2025-4821 applied', user: 'Selin Arslan', type: 'action' },
      { ts: '20:03:29', action: 'Service restarted — incident resolved', user: 'Selin Arslan', type: 'update' },
    ],
  },
  {
    id: 'INC-2026-0034',
    title: 'DoS Attack — External-Facing Load Balancer',
    severity: 'medium',
    status: 'closed',
    assignee: 'Mehmet Yılmaz',
    assigneeInitials: 'MY',
    affectedAssets: ['lb-ext-01', 'dmz-fw-01'],
    firstSeen: '2026-03-15 14:22:31',
    lastUpdated: '2026-03-15 16:45:18',
    mttd: '11.2 min',
    eventCount: 15834,
    mitreTactic: 'Impact',
    mitreId: 'T1499',
    slaBreached: false,
    description: 'Volumetric DoS attack targeting load balancer. DDoS mitigation engaged. Traffic scrubbing reduced impact. Service degradation lasted 8 minutes.',
    timeline: [
      { ts: '14:22:31', action: 'DOS-DETECT-001 alert threshold exceeded', user: 'System', type: 'detection' },
      { ts: '14:33:44', action: 'DDoS mitigation service activated', user: 'Mehmet Yılmaz', type: 'action' },
      { ts: '14:30:18', action: 'Traffic normalized —service restored', user: 'System', type: 'update' },
      { ts: '16:45:18', action: 'Post-incident report filed — incident closed', user: 'Mehmet Yılmaz', type: 'update' },
    ],
  },
];

type SortKey = 'id' | 'severity' | 'status' | 'firstSeen' | 'eventCount' | 'mttd';

const severityOrder: Record<Severity, number> = { critical: 0, high: 1, medium: 2, low: 3, info: 4 };
const statusOrder: Record<IncidentStatus, number> = { new: 0, investigating: 1, contained: 2, resolved: 3, closed: 4 };

export default function IncidentTable() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<IncidentStatus | 'all'>('all');
  const [severityFilter, setSeverityFilter] = useState<Severity | 'all'>('all');
  const [assigneeFilter, setAssigneeFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<SortKey>('id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedIncident, setSelectedIncident] = useState<Incident | null>(null);

  const analysts = Array.from(new Set(incidents.map((i) => i.assignee)));

  const filtered = incidents
    .filter((inc) => {
      if (search) {
        const q = search.toLowerCase();
        if (!inc.id.toLowerCase().includes(q) && !inc.title.toLowerCase().includes(q) && !inc.mitreTactic.toLowerCase().includes(q)) return false;
      }
      if (statusFilter !== 'all' && inc.status !== statusFilter) return false;
      if (severityFilter !== 'all' && inc.severity !== severityFilter) return false;
      if (assigneeFilter !== 'all' && inc.assignee !== assigneeFilter) return false;
      return true;
    })
    .sort((a, b) => {
      let av: any, bv: any;
      if (sortKey === 'severity') { av = severityOrder[a.severity]; bv = severityOrder[b.severity]; }
      else if (sortKey === 'status') { av = statusOrder[a.status]; bv = statusOrder[b.status]; }
      else if (sortKey === 'eventCount') { av = a.eventCount; bv = b.eventCount; }
      else { av = a[sortKey]; bv = b[sortKey]; }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortIcon = ({ col }: { col: SortKey }) => (
    <span className={`ml-1 ${sortKey === col ? 'text-rose-400' : 'text-zinc-700'}`}>
      {sortKey === col ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );

  const handleStatusChange = (inc: Incident, newStatus: IncidentStatus) => {
    // Backend integration: PATCH /api/incidents/:id { status: newStatus }
    toast.success(`${inc.id} status updated to ${newStatus}`);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Open Critical', value: incidents.filter(i => i.severity === 'critical' && i.status !== 'closed' && i.status !== 'resolved').length, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/20' },
          { label: 'Investigating', value: incidents.filter(i => i.status === 'investigating').length, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/20' },
          { label: 'SLA Breached', value: incidents.filter(i => i.slaBreached).length, color: 'text-orange-400', bg: 'bg-orange-500/10 border-orange-500/20' },
          { label: 'Resolved Today', value: incidents.filter(i => i.status === 'resolved' || i.status === 'closed').length, color: 'text-emerald-400', bg: 'bg-emerald-500/10 border-emerald-500/20' },
        ].map((s) => (
          <div key={s.label} className={`bg-zinc-900 border ${s.bg} rounded-xl px-4 py-3`}>
            <p className="text-[11px] font-medium uppercase tracking-widest text-zinc-500">{s.label}</p>
            <p className={`text-2xl font-bold tabular-nums mt-1 ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-48">
          <Icon name="MagnifyingGlassIcon" size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Search incidents, tactics..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-8 pr-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-300 placeholder-zinc-600 focus:outline-none focus:border-rose-500/50 focus:ring-1 focus:ring-rose-500/30"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-400 focus:outline-none focus:border-rose-500/50 cursor-pointer"
        >
          <option value="all">All Statuses</option>
          {(['new', 'investigating', 'contained', 'resolved', 'closed'] as IncidentStatus[]).map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        <select
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value as any)}
          className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-400 focus:outline-none focus:border-rose-500/50 cursor-pointer"
        >
          <option value="all">All Severities</option>
          {(['critical', 'high', 'medium', 'low'] as Severity[]).map((s) => (
            <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
          ))}
        </select>
        <select
          value={assigneeFilter}
          onChange={(e) => setAssigneeFilter(e.target.value)}
          className="px-3 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-xs text-zinc-400 focus:outline-none focus:border-rose-500/50 cursor-pointer"
        >
          <option value="all">All Analysts</option>
          {analysts.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <div className="ml-auto">
          <button className="flex items-center gap-1.5 px-3 py-2 bg-rose-600 hover:bg-rose-500 text-white text-xs font-semibold rounded-lg transition-colors active:scale-95">
            <Icon name="PlusIcon" size={13} />
            New Incident
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-zinc-800">
                {([
                  { key: 'id', label: 'Incident ID' },
                  { key: null, label: 'Title' },
                  { key: 'severity', label: 'Severity' },
                  { key: 'status', label: 'Status' },
                  { key: null, label: 'Assignee' },
                  { key: null, label: 'Affected Assets' },
                  { key: 'firstSeen', label: 'First Seen' },
                  { key: 'mttd', label: 'MTTD' },
                  { key: 'eventCount', label: 'Events' },
                  { key: null, label: 'MITRE Tactic' },
                  { key: null, label: 'SLA' },
                  { key: null, label: 'Actions' },
                ] as { key: SortKey | null; label: string }[]).map(({ key, label }) => (
                  <th
                    key={label}
                    onClick={() => key && handleSort(key)}
                    className={`px-3 py-3 text-left font-semibold text-zinc-600 uppercase tracking-widest text-[10px] whitespace-nowrap ${key ? 'cursor-pointer hover:text-zinc-400 select-none' : ''}`}
                  >
                    {label}{key && <SortIcon col={key} />}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((inc, idx) => (
                <tr
                  key={inc.id}
                  onClick={() => setSelectedIncident(inc)}
                  className={`
                    border-b border-zinc-800/40 hover:bg-zinc-800/40 transition-colors cursor-pointer group
                    ${idx % 2 !== 0 ? 'bg-zinc-900/40' : ''}
                    ${inc.slaBreached ? 'border-l-2 border-l-orange-500' : ''}
                    ${selectedIncident?.id === inc.id ? 'bg-rose-500/5' : ''}
                  `}
                >
                  <td className="px-3 py-3 font-mono text-zinc-400 whitespace-nowrap">{inc.id}</td>
                  <td className="px-3 py-3 max-w-xs">
                    <p className="text-zinc-200 font-medium truncate">{inc.title}</p>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap"><SeverityBadge severity={inc.severity} /></td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="relative group/status" onClick={(e) => e.stopPropagation()}>
                      <StatusBadge status={inc.status} />
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
                        {inc.assigneeInitials}
                      </div>
                      <span className="text-zinc-400 text-xs">{inc.assignee.split(' ')[0]}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1">
                      {inc.affectedAssets.slice(0, 2).map((a) => (
                        <span key={a} className="font-mono text-[10px] bg-zinc-800 text-zinc-500 px-1.5 py-0.5 rounded border border-zinc-700">
                          {a.length > 18 ? a.slice(0, 18) + '…' : a}
                        </span>
                      ))}
                      {inc.affectedAssets.length > 2 && (
                        <span className="text-[10px] text-zinc-600">+{inc.affectedAssets.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 font-mono text-zinc-500 whitespace-nowrap text-[11px]">{inc.firstSeen}</td>
                  <td className="px-3 py-3 font-mono text-zinc-400 whitespace-nowrap">{inc.mttd}</td>
                  <td className="px-3 py-3 font-mono text-zinc-400 whitespace-nowrap tabular-nums">{inc.eventCount.toLocaleString()}</td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-mono text-purple-400 bg-purple-500/10 border border-purple-500/20 px-1.5 py-0.5 rounded">
                        {inc.mitreId}
                      </span>
                      <span className="text-[11px] text-zinc-500">{inc.mitreTactic}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap">
                    {inc.slaBreached ? (
                      <span className="flex items-center gap-1 text-[10px] font-semibold text-orange-400 bg-orange-500/10 border border-orange-500/20 px-1.5 py-0.5 rounded uppercase tracking-wide">
                        <Icon name="ExclamationTriangleIcon" size={10} />
                        Breached
                      </span>
                    ) : (
                      <span className="text-[10px] text-emerald-500">✓ On time</span>
                    )}
                  </td>
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="relative group/tip">
                        <button
                          onClick={() => setSelectedIncident(inc)}
                          className="p-1.5 rounded hover:bg-zinc-700 text-zinc-600 hover:text-zinc-300 transition-colors"
                          aria-label="View incident details"
                        >
                          <Icon name="EyeIcon" size={13} />
                        </button>
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[10px] text-zinc-300 whitespace-nowrap opacity-0 group-hover/tip:opacity-100 pointer-events-none z-10">
                          View details
                        </span>
                      </div>
                      <div className="relative group/tip">
                        <button
                          onClick={() => handleStatusChange(inc, 'resolved')}
                          className="p-1.5 rounded hover:bg-zinc-700 text-zinc-600 hover:text-emerald-400 transition-colors"
                          aria-label="Mark resolved"
                        >
                          <Icon name="CheckCircleIcon" size={13} />
                        </button>
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded text-[10px] text-zinc-300 whitespace-nowrap opacity-0 group-hover/tip:opacity-100 pointer-events-none z-10">
                          Mark resolved
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-zinc-800">
          <span className="text-xs text-zinc-600 font-mono">{filtered.length} incident{filtered.length !== 1 ? 's' : ''} matching filters</span>
        </div>
      </div>

      {/* Detail Panel */}
      {selectedIncident && (
        <IncidentDetailPanel incident={selectedIncident} onClose={() => setSelectedIncident(null)} />
      )}
    </div>
  );
}